import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageShell from '../../components/PageShell';
import CardShell from '../../components/CardShell';
import Tabs from '../../components/Tabs';
import { useViewExcelExport } from '../../components/ExcelExportButton';
import { useRegisterPageExports } from '../../exports/pageExports';
import { useP } from '../../i18n/useP';
import { formatNumber } from '../../utils/format';
import { Cell, Choix } from '../../components/InlineCells';
import { addDeal, carryForward, deleteDeal, getCoverage, setCoverageSettings, setDealYear, updateDeal }
  from '../../api/salesDeals';
import './SalesPlanPage.css';

/* ── Plan des ventes (2026-09-09) ───────────────────────────────────────────
   Les affaires qui soutiennent la prévision, en trois catégories jamais
   mélangées : commandes signées, offres remises, opportunités. Un onglet
   chacune, et la Synthèse qui les réunit en est un aussi : une bascule de vue
   à côté d'une ligne d'onglets faisait deux commandes pour un seul choix.

   Tout se lit EN COLONNES, sans panneau à ouvrir :
     valeur totale + modification − réalisé = SOLDE
   Le solde est la grandeur cherchée. Il se referme aussitôt en deux totaux
   d'année et le solde à reporter, TOUJOURS les deux années : c'est l'équation
   complète, et les deux totaux d'année se saisissent.

   AUCUN MOIS ICI : le calendrier vit dans le Prévisionnel, qui dit ce qu'il
   faut atteindre. Le vendeur pose ce que l'affaire apporte à chaque exercice,
   et le solde à reporter tombe sur l'année suivante d'un geste explicite.

   L'objectif ne se tape pas non plus : c'est le Prévisionnel. Le vendeur
   constate le solde à gagner, il ne redéfinit pas la cible.

   La table se groupe comme la Trajectoire : compte de CA, puis offre, puis
   affaires. */

const CATEGORIES = ['order', 'quote', 'opportunity'];
const ONGLETS = ['synth', ...CATEGORIES];
const libelleCat = (p, c) => ({
  synth: p('Synthèse'),
  order: p('Commandes'), quote: p('Offres remises'), opportunity: p('Opportunités'),
}[c] || c);
const sousTitreCat = (p, c) => ({
  order: p('signé, reste à réaliser'), quote: p('proposé, pas encore gagné'),
  opportunity: p('repéré, sans offre remise'),
}[c] || '');
export default function SalesPlanPage({ activeTenant, year, addToast, canWrite = false }) {
  const p = useP();
  const tid = activeTenant?.id;
  const [view, setView] = useState(null);
  const [cat, setCat] = useState('synth');          // synth · order · quote · opportunity
  const [unit, setUnit] = useState('kchf');
  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);   // bande de contexte, sur la ligne de l'exercice
  const exportRef = useRef(null);

  const reload = useCallback(async () => {
    if (!tid || !year) return;
    const r = await getCoverage(tid, year);
    if (r.error) { addToast && addToast(r.error, 'error'); return; }
    setView(r.data);
  }, [tid, year, addToast]);
  useEffect(() => { reload(); }, [reload]);

  const excel = useViewExcelExport({ ref: exportRef, sheetName: p('Plan des ventes'), fileBase: p('Plan des ventes'), year, addToast });
  const excelRun = useRef(null); excelRun.current = excel.run;
  const exportOptions = useMemo(() => [{ id: 'excel', label: 'Excel', run: () => excelRun.current && excelRun.current() }], []);
  useRegisterPageExports(exportOptions);

  const appel = async (promesse) => { const r = await promesse; if (r.error) addToast && addToast(r.error, 'error'); await reload(); };

  const an = view?.years || [year, year + 1];
  const synth = cat === 'synth';
  const nb = (v) => (v == null ? '' : formatNumber(unit === 'kchf' ? v / 1000 : v, 0));
  const val = (v) => (v == null ? '–' : nb(v));
  const brut = (v) => (v == null ? null : (unit === 'kchf' ? v * 1000 : v));
  const draft = (v) => (v == null ? '' : String(Math.round(unit === 'kchf' ? v / 1000 : v)));
  const signe = (v) => (v == null || v === 0 ? '–' : <span className={v < 0 ? 'sp-neg' : ''}>{v > 0 ? '+' : ''}{nb(v)}</span>);
  const bloc = (c) => (view?.blocks || []).find((b) => b.category === c);

  const L = 88;
  const largeur = 246 + (5 + an.length) * L + 34;
  const colGroup = (
    <colgroup>
      <col style={{ width: 246 }} />
      {Array.from({ length: 5 + an.length }, (_, i) => <col key={`v${i}`} style={{ width: L }} />)}
      <col style={{ width: 34 }} />
    </colgroup>
  );
  const nbCols = 5 + an.length + 1 + 1;

  const enTete = (
    <thead>
      {/* Les totaux d'année et ce qui se reporte forment un tout : le bandeau
          couvre les trois colonnes, c'est le solde ouvert vu par exercice. */}
      <tr className="sp-band">
        <th colSpan={5} />
        <th colSpan={an.length + 1} className="sp-fact">{p('À facturer')}</th>
        <th />
      </tr>
      <tr>
        <th className="sp-lbl">{p('Affaire')} · {unit === 'kchf' ? 'kCHF' : 'CHF'}</th>
        <th>{p('Valeur')}</th><th>{p('Modification')}</th><th>{p('Réalisé')}</th>
        <th className="sp-solde">{p('Solde ouvert')}</th>
        {an.map((y) => <th key={y} className="sp-year">{y}</th>)}
        <th>{p('Solde reporté')}</th>
        <th />
      </tr>
    </thead>
  );

  const ligne = (r) => (
    <tr className="sp-row" key={r.id}>
      <td className="sp-lbl">
        <Cell value={r.label} canWrite={canWrite} onSave={(v) => appel(updateDeal(tid, r.id, { label: v }))} />
      </td>
      <td className="lk-num">
        <Cell value={r.known} type="number" canWrite={canWrite} format={(v) => nb(v)} toDraft={draft}
              placeholder="…" onSave={(v) => appel(updateDeal(tid, r.id, { known_amount: brut(v) }))} />
      </td>
      <td className="lk-num">
        <Cell value={r.adjustment} type="number" canWrite={canWrite}
              format={(v) => (v == null || v === 0 ? '' : `${v > 0 ? '+' : ''}${nb(v)}`)} toDraft={draft}
              placeholder="…" onSave={(v) => appel(updateDeal(tid, r.id, { adjustment: brut(v) }))} />
      </td>
      <td className="lk-num">
        <Cell value={r.actual} type="number" canWrite={canWrite} format={(v) => nb(v)} toDraft={draft}
              placeholder="…" onSave={(v) => appel(updateDeal(tid, r.id, { actual: brut(v) }))} />
      </td>
      <td className="lk-num sp-solde">{val(r.remaining)}</td>
      {an.map((y) => (
        <td key={y} className="lk-num sp-year">
          <Cell value={r.years[String(y)] || null} type="number" canWrite={canWrite}
                format={(v) => (v ? nb(v) : '')} toDraft={draft} placeholder="…"
                onSave={(v) => appel(setDealYear(tid, r.id, y, brut(v)))} />
        </td>
      ))}
      <td className="lk-num">
        {r.to_carry < 0 ? <span className="sp-neg">{nb(r.to_carry)}</span> : val(r.to_carry)}
        {canWrite && r.to_carry > 0 && (
          <button type="button" className="sp-carry" title={`${p('Reporter sur')} ${an[1]}`}
                  onClick={() => appel(carryForward(tid, r.id, an[0]))}>⇥</button>
        )}
      </td>
      <td className="lk-actions">
        {canWrite && <button type="button" className="lk-x lk-del" title={p("Retirer l'affaire")}
                             onClick={() => appel(deleteDeal(tid, r.id))}>✕</button>}
      </td>
    </tr>
  );

  const groupe = (g) => (
    <React.Fragment key={`${g.line_id || 'sans'}-${g.offer_id}`}>
      <tr className="sp-grp">
        <td className="sp-lbl">
          {g.offer_name}
          <span className="sp-account">{g.line_label || p('sans compte')}</span>
        </td>
        <td className="lk-num">{val(g.totals.known)}</td>
        <td className="lk-num">{signe(g.totals.adjustment)}</td>
        <td className="lk-num">{val(g.totals.actual)}</td>
        <td className="lk-num sp-solde">{val(g.totals.remaining)}</td>
        {an.map((y) => <td key={y} className="lk-num sp-year">{val(g.totals.years[String(y)])}</td>)}
        <td className="lk-num">{val(g.totals.to_carry)}</td>
        <td />
      </tr>
      {g.rows.map(ligne)}
    </React.Fragment>
  );

  const b = bloc(cat);
  const detail = b && (
    <div className="sp-scroll lk-hscroll">
      <table className="lk-entry-table sp-table" style={{ width: largeur }}>
        {colGroup}
        {enTete}
        <tbody>
          {b.groups.length === 0 && (
            <tr><td className="sp-vide" colSpan={nbCols}>
              {{ order: p('Aucune commande en carnet.'), quote: p('Aucune offre remise.'),
                 opportunity: p('Aucune opportunité identifiée.') }[cat]}
            </td></tr>
          )}
          {b.groups.map(groupe)}
          {b.groups.length > 0 && (
            <tr className="sp-tot">
              <td className="sp-lbl">{p('Total')}</td>
              <td className="lk-num">{val(b.known)}</td>
              <td className="lk-num">{signe(b.adjustment)}</td>
              <td className="lk-num">{val(b.actual)}</td>
              <td className="lk-num sp-solde">{val(b.remaining)}</td>
              {an.map((y) => <td key={y} className="lk-num sp-year">{val(b.years[String(y)])}</td>)}
              <td className="lk-num">{val(b.to_carry)}</td>
              <td />
            </tr>
          )}
          {/* Le pondéré ne remplace jamais le brut : il se lit sous lui. */}
          {b.probability != null && b.groups.length > 0 && (
            <tr className="sp-pond">
              <td className="sp-lbl">{p('Retenu après probabilité')}</td>
              <td className="lk-num" colSpan={4}>
                <Cell value={b.probability} type="number" canWrite={canWrite}
                      format={(v) => (v == null ? '' : `${formatNumber(v, 0)} %`)} placeholder="…"
                      onSave={(v) => appel(setCoverageSettings(tid, year,
                        cat === 'quote' ? { quote_pct: v } : { opportunity_pct: v }))} />
              </td>
              {an.map((y) => <td key={y} className="lk-num sp-year">{val(b.weighted[String(y)])}</td>)}
              <td colSpan={2} />
            </tr>
          )}
          {canWrite && (view?.offers || []).length > 0 && (
            <tr className="lk-add-row">
              <td colSpan={nbCols}>
                <button type="button" className="lk-add" onClick={() => appel(addDeal(tid, {
                  offer_id: view.offers[0].id, label: p('Nouvelle affaire'), category: cat,
                }))}>＋ {p('Affaire')}</button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const synthese = view && (
    <table className="lk-entry-table sp-table sp-sum">
      <colgroup><col /><col style={{ width: 120 }} /><col style={{ width: 120 }} /></colgroup>
      <thead><tr><th className="sp-lbl" /><th className="lk-num">{an[0]}</th><th className="lk-num">{an[1]}</th></tr></thead>
      <tbody>
        {CATEGORIES.map((c) => {
          const x = bloc(c);
          return (
            <React.Fragment key={c}>
              <tr className="sp-s1">
                <td className="sp-lbl">{libelleCat(p, c)}</td>
                <td className="lk-num">{val(x.years[String(an[0])])}</td>
                <td className="lk-num">{val(x.years[String(an[1])])}</td>
              </tr>
              {x.probability != null && (
                <tr>
                  <td className="sp-lbl sp-pad">{p('retenu à')} {formatNumber(x.probability, 0)} %</td>
                  <td className="lk-num">{val(x.weighted[String(an[0])])}</td>
                  <td className="lk-num">{val(x.weighted[String(an[1])])}</td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
        <tr className="sp-s2">
          <td className="sp-lbl">{p('Prévisionnel identifié')}</td>
          <td className="lk-num">{val(view.identified[String(an[0])])}</td>
          <td className="lk-num">{val(view.identified[String(an[1])])}</td>
        </tr>
        <tr className="sp-gap"><td colSpan={3} /></tr>
        <tr className="sp-s2">
          <td className="sp-lbl">{p('Objectif du Prévisionnel')}</td>
          <td className="lk-num">{val(view.objective[String(an[0])])}</td>
          <td className="lk-num">{val(view.objective[String(an[1])])}</td>
        </tr>
        <tr className="sp-s3">
          <td className="sp-lbl">{p('Reste à vendre')}</td>
          <td className="lk-num">{val(view.left_to_sell[String(an[0])])}</td>
          <td className="lk-num">{val(view.left_to_sell[String(an[1])])}</td>
        </tr>
      </tbody>
    </table>
  );

  if (!tid) {
    return <PageShell framed title={p('Plan des ventes')}><p className="sp-empty">{p('Choisir un client.')}</p></PageShell>;
  }
  return (
    <PageShell framed title={p('Plan des ventes')} exportRef={exportRef} unit={pageUnit}>
      <Tabs tabs={ONGLETS.map((c) => ({ id: c, label: libelleCat(p, c) }))} active={cat} onChange={setCat} />
      <CardShell title={libelleCat(p, cat)}
                 subtitle={synth ? `${an[0]} · ${an[1]}` : sousTitreCat(p, cat)}
                 flush={!synth}
>
        {synth ? synthese
          : (view && (view.offers || []).length === 0
            ? <div className="sp-empty">{p("Le catalogue de l'Offre est vide : une affaire s'y rattache.")}</div>
            : detail)}
      </CardShell>
    </PageShell>
  );
}
